import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { api } from "../api/client";
import BottomNav from "../components/BottomNav";
import DropdownSelect from "../components/DropdownSelect";
import { useDropdownOptions } from "../hooks/useDropdownOptions";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ExpenseCategory {
  id: number;
  name: string;
  requires_receipt: boolean;
  max_amount: number | null;
}

interface ExpenseItem {
  id: number;
  claim_id: number;
  category_id: number;
  description: string;
  amount: number;
  currency: string;
  expense_date: string;
  payment_method: string | null;
  receipt_blob_url: string | null;
  invoice_blob_url: string | null;
  invoice_file_name: string | null;
  mileage_km: number | null;
  mileage_rate: number | null;
  notes: string | null;
  created_at: string | null;
}

interface ExpenseClaim {
  id: number;
  employee_id: number;
  title: string;
  description: string | null;
  status: string;
  total_amount: number;
  currency: string;
  submitted_at: string | null;
  approved_by_id: number | null;
  approved_by_name?: string | null;
  approved_at: string | null;
  rejected_reason: string | null;
  paid_at: string | null;
  created_at: string | null;
  items?: ExpenseItem[];
}

type View = "list" | "create" | "detail";

const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: "£",
  EUR: "€",
  ZAR: "R",
  USD: "$",
};

const STATUS_TONE: Record<string, string> = {
  draft: "bg-gray-100 text-gray-600",
  submitted: "bg-amber-50 text-amber-800",
  approved: "bg-green-50 text-green-700",
  rejected: "bg-red-50 text-red-700",
  paid: "bg-blue-50 text-blue-700",
};

const STATUS_LABEL: Record<string, string> = {
  draft: "Draft",
  submitted: "Submitted",
  approved: "Approved",
  rejected: "Rejected",
  paid: "Paid",
};

const DATE_FMT = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });

function currencySymbol(code: string): string {
  return CURRENCY_SYMBOLS[code] ?? code;
}

function fmtMoney(amount: number, currency: string): string {
  const sym = currencySymbol(currency);
  return `${sym}${amount.toFixed(2)}`;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ExpensesPage() {
  const { t } = useTranslation();
  const [view, setView] = useState<View>("list");
  const [claims, setClaims] = useState<ExpenseClaim[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [activeClaim, setActiveClaim] = useState<ExpenseClaim | null>(null);
  const [loading, setLoading] = useState(true);

  const defaultCurrency = "GBP";

  const loadClaims = useCallback(async () => {
    try {
      const url = statusFilter
        ? `/api/expenses/my?status=${statusFilter}`
        : "/api/expenses/my";
      setClaims(await api.get<ExpenseClaim[]>(url));
    } catch {
      /* empty */
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    void loadClaims();
  }, [loadClaims]);

  useEffect(() => {
    void api
      .get<ExpenseCategory[]>("/api/expenses/categories")
      .then(setCategories)
      .catch(() => {});
  }, []);

  const openClaim = async (id: number) => {
    try {
      const claim = await api.get<ExpenseClaim>(`/api/expenses/${id}`);
      setActiveClaim(claim);
      setView("detail");
    } catch {
      /* empty */
    }
  };

  const refreshClaim = async (id: number) => {
    try {
      const claim = await api.get<ExpenseClaim>(`/api/expenses/${id}`);
      setActiveClaim(claim);
    } catch {
      /* empty */
    }
  };

  // --- List view -----------------------------------------------------------

  if (view === "list") {
    return (
      <div className="flex min-h-dvh flex-col bg-white safe-x">
        <header className="safe-top px-5 pt-4 pb-3">
          <div className="flex items-center justify-between pr-28">
            <h1 className="text-2xl font-semibold text-gray-900">
              {t("nav.expenses")}
            </h1>
            <button
              type="button"
              onClick={() => setView("create")}
              className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white"
            >
              + New Claim
            </button>
          </div>
          <div className="mt-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input text-sm"
            >
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="paid">Paid</option>
            </select>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto px-5 pb-24 space-y-3">
          {loading && (
            <p className="text-sm text-gray-400">{t("common.loading")}</p>
          )}
          {!loading && claims.length === 0 && (
            <p className="text-sm text-gray-400">{t("common.noData")}</p>
          )}
          {claims.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => void openClaim(c.id)}
              className="w-full rounded-xl border border-gray-200 bg-white p-4 text-left shadow-sm hover:border-gray-300 transition-colors"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {c.title}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {fmtMoney(c.total_amount, c.currency)}
                    {c.created_at &&
                      ` · ${DATE_FMT.format(new Date(c.created_at))}`}
                  </p>
                </div>
                <span
                  className={`shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${STATUS_TONE[c.status] ?? "bg-gray-100"}`}
                >
                  {STATUS_LABEL[c.status] ?? c.status}
                </span>
              </div>
            </button>
          ))}
        </main>
        <BottomNav />
      </div>
    );
  }

  // --- Create view ---------------------------------------------------------

  if (view === "create") {
    return (
      <CreateClaimView
        defaultCurrency={defaultCurrency}
        onCreated={(claim) => {
          setActiveClaim(claim);
          setView("detail");
          void loadClaims();
        }}
        onCancel={() => setView("list")}
      />
    );
  }

  // --- Detail view ---------------------------------------------------------

  if (view === "detail" && activeClaim) {
    return (
      <ClaimDetailView
        claim={activeClaim}
        categories={categories}
        defaultCurrency={defaultCurrency}
        onRefresh={() => void refreshClaim(activeClaim.id)}
        onBack={() => {
          setView("list");
          setActiveClaim(null);
          void loadClaims();
        }}
        onDeleted={() => {
          setView("list");
          setActiveClaim(null);
          void loadClaims();
        }}
      />
    );
  }

  return null;
}

// ---------------------------------------------------------------------------
// Create claim form
// ---------------------------------------------------------------------------

function CreateClaimView({
  defaultCurrency,
  onCreated,
  onCancel,
}: {
  defaultCurrency: string;
  onCreated: (claim: ExpenseClaim) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [currency, setCurrency] = useState(defaultCurrency);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!title.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const claim = await api.post<ExpenseClaim>("/api/expenses", {
        title: title.trim(),
        description: description.trim() || null,
        currency,
      });
      onCreated(claim);
    } catch (err) {
      setError((err as Error).message || "Failed to create");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-dvh flex-col bg-white safe-x">
      <header className="safe-top px-5 pt-4 pb-3 flex items-center gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="text-sm text-gray-500"
        >
          {t("common.cancel")}
        </button>
        <h1 className="text-lg font-semibold text-gray-900 flex-1">
          New Expense Claim
        </h1>
      </header>

      <main className="flex-1 overflow-y-auto px-5 pb-24 space-y-4">
        {error && (
          <p className="text-sm text-red-600 bg-red-50 rounded-lg p-3">
            {error}
          </p>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title
          </label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="input"
            placeholder="e.g. London client visit — June 2026"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description (optional)
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="input"
            rows={3}
            placeholder="Add any additional notes..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Currency
          </label>
          <DropdownSelect
            listCode="currency"
            value={currency}
            onChange={setCurrency}
          />
        </div>

        <button
          type="button"
          onClick={() => void submit()}
          disabled={busy || !title.trim()}
          className="w-full rounded-lg bg-pme-blue px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"
        >
          {busy ? "Creating..." : "Create Claim"}
        </button>
      </main>
      <BottomNav />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Claim detail / edit view
// ---------------------------------------------------------------------------

function ClaimDetailView({
  claim,
  categories,
  onRefresh,
  onBack,
  onDeleted,
}: {
  claim: ExpenseClaim;
  categories: ExpenseCategory[];
  defaultCurrency: string;
  onRefresh: () => void;
  onBack: () => void;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const paymentMethodOptions = useDropdownOptions("expense_payment_method");
  const isDraft = claim.status === "draft";
  const items = claim.items ?? [];
  const [showItemForm, setShowItemForm] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async () => {
    if (!confirm("Submit this claim for approval?")) return;
    setBusy(true);
    try {
      await api.put(`/api/expenses/${claim.id}/submit`);
      onRefresh();
    } catch (err) {
      alert((err as Error).message || "Failed to submit");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm("Delete this draft claim? This cannot be undone.")) return;
    try {
      await api.delete(`/api/expenses/${claim.id}`);
      onDeleted();
    } catch (err) {
      alert((err as Error).message || "Failed to delete");
    }
  };

  const handleDeleteItem = async (itemId: number) => {
    if (!confirm("Remove this item?")) return;
    try {
      await api.delete(`/api/expenses/items/${itemId}`);
      onRefresh();
    } catch (err) {
      alert((err as Error).message || "Failed to delete item");
    }
  };

  return (
    <div className="flex min-h-dvh flex-col bg-white safe-x">
      <header className="safe-top px-5 pt-4 pb-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="text-sm text-gray-500"
          >
            ← {t("common.back")}
          </button>
          <div className="flex-1" />
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_TONE[claim.status] ?? "bg-gray-100"}`}
          >
            {STATUS_LABEL[claim.status] ?? claim.status}
          </span>
        </div>
        <h1 className="mt-2 text-xl font-semibold text-gray-900">
          {claim.title}
        </h1>
        {claim.description && (
          <p className="mt-1 text-sm text-gray-500">{claim.description}</p>
        )}
      </header>

      <main className="flex-1 overflow-y-auto px-5 pb-24 space-y-4">
        {/* Rejection reason */}
        {claim.status === "rejected" && claim.rejected_reason && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs font-medium text-red-800 mb-1">
              Rejection reason
            </p>
            <p className="text-sm text-red-700">{claim.rejected_reason}</p>
          </div>
        )}

        {/* Approval info */}
        {(claim.status === "approved" || claim.status === "paid") &&
          claim.approved_at && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">
              Approved
              {claim.approved_by_name && ` by ${claim.approved_by_name}`}
              {` on ${DATE_FMT.format(new Date(claim.approved_at))}`}
              {claim.status === "paid" && claim.paid_at && (
                <span>
                  {" "}
                  · Paid {DATE_FMT.format(new Date(claim.paid_at))}
                </span>
              )}
            </div>
          )}

        {/* Items list */}
        {items.length === 0 && !showItemForm && (
          <p className="text-sm text-gray-400 py-4 text-center">
            No items yet. Add your first expense item below.
          </p>
        )}

        {items.map((item) => {
          const cat = categories.find((c) => c.id === item.category_id);
          const pm = paymentMethodOptions.find(
            (m) => m.value === item.payment_method,
          );
          return (
            <div
              key={item.id}
              className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {cat && (
                      <span className="text-xs text-gray-400 bg-gray-50 rounded px-1.5 py-0.5">
                        {cat.name}
                      </span>
                    )}
                    <span className="text-xs text-gray-400">
                      {DATE_FMT.format(new Date(item.expense_date))}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-gray-900 mt-1">
                    {item.description}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    {pm && (
                      <span className="text-xs text-gray-500">
                        {pm.label}
                      </span>
                    )}
                    {item.mileage_km != null && (
                      <span className="text-xs text-gray-500">
                        {item.mileage_km} km
                        {item.mileage_rate != null &&
                          ` @ ${currencySymbol(item.currency)}${item.mileage_rate}/km`}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {item.receipt_blob_url && (
                      <a
                        href={item.receipt_blob_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-pme-blue underline"
                      >
                        View receipt
                      </a>
                    )}
                    {item.invoice_blob_url && (
                      <a
                        href={item.invoice_blob_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-pme-blue underline"
                      >
                        {item.invoice_file_name ?? "View invoice"}
                      </a>
                    )}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-sm font-semibold text-gray-900">
                    {fmtMoney(item.amount, item.currency)}
                  </p>
                  {isDraft && (
                    <button
                      type="button"
                      onClick={() => void handleDeleteItem(item.id)}
                      className="mt-1 text-xs text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Running total */}
        {items.length > 0 && (
          <div className="flex justify-between items-center px-1 py-2 border-t border-gray-200">
            <span className="text-sm font-medium text-gray-700">
              Total ({items.length} {items.length === 1 ? "item" : "items"})
            </span>
            <span className="text-lg font-bold text-gray-900">
              {fmtMoney(claim.total_amount, claim.currency)}
            </span>
          </div>
        )}

        {/* Add item form */}
        {isDraft && showItemForm && (
          <AddItemForm
            claimId={claim.id}
            currency={claim.currency}
            categories={categories}
            onAdded={() => {
              setShowItemForm(false);
              onRefresh();
            }}
            onCancel={() => setShowItemForm(false)}
          />
        )}

        {/* Action buttons */}
        {isDraft && !showItemForm && (
          <div className="space-y-2 pt-2">
            <button
              type="button"
              onClick={() => setShowItemForm(true)}
              className="w-full rounded-lg border-2 border-dashed border-gray-300 px-4 py-3 text-sm font-medium text-gray-600 hover:border-gray-400 hover:text-gray-700"
            >
              + Add Item
            </button>

            {items.length > 0 && (
              <button
                type="button"
                onClick={() => void handleSubmit()}
                disabled={busy}
                className="w-full rounded-lg bg-pme-blue px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"
              >
                {busy ? "Submitting..." : "Submit for Approval"}
              </button>
            )}

            <button
              type="button"
              onClick={() => void handleDelete()}
              className="w-full rounded-lg border border-red-200 px-4 py-3 text-sm text-red-600 hover:bg-red-50"
            >
              Delete Claim
            </button>
          </div>
        )}
      </main>
      <BottomNav />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add item form
// ---------------------------------------------------------------------------

function AddItemForm({
  claimId,
  currency,
  categories,
  onAdded,
  onCancel,
}: {
  claimId: number;
  currency: string;
  categories: ExpenseCategory[];
  onAdded: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [categoryId, setCategoryId] = useState<number | "">(
    categories[0]?.id ?? "",
  );
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [expenseDate, setExpenseDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [paymentMethod, setPaymentMethod] = useState("");
  const [mileageKm, setMileageKm] = useState("");
  const [mileageRate, setMileageRate] = useState("0.45");
  const [receipt, setReceipt] = useState<File | null>(null);
  const [invoice, setInvoice] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const receiptRef = useRef<HTMLInputElement>(null);
  const invoiceRef = useRef<HTMLInputElement>(null);

  const selectedCat = categories.find((c) => c.id === categoryId);
  const isMileage =
    selectedCat?.name.toLowerCase().includes("mileage") ?? false;

  const submit = async () => {
    if (!categoryId || !description.trim() || !amount || !expenseDate) return;

    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("category_id", String(categoryId));
      form.append("description", description.trim());
      form.append("amount", amount);
      form.append("expense_date", expenseDate);
      form.append("currency", currency);
      if (paymentMethod) form.append("payment_method", paymentMethod);
      if (isMileage && mileageKm)
        form.append("mileage_km", mileageKm);
      if (isMileage && mileageRate)
        form.append("mileage_rate", mileageRate);
      if (receipt) form.append("receipt", receipt);
      if (invoice) form.append("invoice", invoice);

      await api.postFormData(`/api/expenses/${claimId}/items`, form);
      onAdded();
    } catch (err) {
      setError((err as Error).message || "Failed to add item");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3">
      <h3 className="text-sm font-semibold text-gray-800">Add Item</h3>

      {error && (
        <p className="text-xs text-red-600 bg-red-50 rounded p-2">{error}</p>
      )}

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Category
        </label>
        <select
          value={categoryId}
          onChange={(e) => setCategoryId(Number(e.target.value))}
          className="input text-sm"
        >
          <option value="">Select...</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Description
        </label>
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="input text-sm"
          placeholder="e.g. Train ticket London to Chichester"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Amount ({currencySymbol(currency)})
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="input text-sm"
            placeholder="0.00"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Date
          </label>
          <input
            type="date"
            value={expenseDate}
            onChange={(e) => setExpenseDate(e.target.value)}
            className="input text-sm"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Payment Method
        </label>
        <DropdownSelect
          listCode="expense_payment_method"
          value={paymentMethod}
          onChange={setPaymentMethod}
          placeholder="Select..."
          className="input text-sm"
        />
      </div>

      {isMileage && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Distance (km)
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              value={mileageKm}
              onChange={(e) => setMileageKm(e.target.value)}
              className="input text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Rate per km ({currencySymbol(currency)})
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={mileageRate}
              onChange={(e) => setMileageRate(e.target.value)}
              className="input text-sm"
            />
          </div>
        </div>
      )}

      {/* File uploads */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <input
            ref={receiptRef}
            type="file"
            accept="image/jpeg,image/png,application/pdf"
            className="hidden"
            onChange={(e) => setReceipt(e.target.files?.[0] ?? null)}
          />
          <button
            type="button"
            onClick={() => receiptRef.current?.click()}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-xs text-gray-600 hover:bg-gray-50"
          >
            {receipt ? (
              <span className="truncate block">{receipt.name}</span>
            ) : (
              "Upload Receipt"
            )}
          </button>
        </div>
        <div>
          <input
            ref={invoiceRef}
            type="file"
            accept="application/pdf,image/jpeg,image/png"
            className="hidden"
            onChange={(e) => setInvoice(e.target.files?.[0] ?? null)}
          />
          <button
            type="button"
            onClick={() => invoiceRef.current?.click()}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-xs text-gray-600 hover:bg-gray-50"
          >
            {invoice ? (
              <span className="truncate block">{invoice.name}</span>
            ) : (
              "Upload Invoice"
            )}
          </button>
        </div>
      </div>

      {/* Submit / Cancel */}
      <div className="flex gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600"
        >
          {t("common.cancel")}
        </button>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={busy || !categoryId || !description.trim() || !amount}
          className="flex-1 rounded-lg bg-pme-blue px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
        >
          {busy ? "Adding..." : "Add Item"}
        </button>
      </div>
    </div>
  );
}
